<template>
  <div>
    <!-- Loading Message -->
    <b-skeleton-table
      v-if="isLoading"
      :rows="10"
      :columns="columns.length"
      :table-props="{ bordered: true, striped: true }"
    />

    <!-- Vue Data Table -->
    <vue-good-table
      v-if="!isLoading"
      :mode="remoteMode ? 'remote': ''"
      :columns="columns"
      :rows="data.rows"
      :search-options="{
        enabled: true,
        externalQuery: data.searchTerm
      }"
      :sort-options="{
        enabled: enableSort,
      }"
      :pagination-options="{
        enabled: enablePagination,
        perPage: data.pageLength
      }"
      @on-sort-change="onSortChange"
    >
      <template
        v-if="typeOfTable === 'translateLanguages'"
        slot="table-column"
        slot-scope="data"
      >
        <span
          v-if="data.column.field != 'resourceName'"
          class="d-flex align-items-center"
        >
          <country-flag
            :country="data.column.flag"
            size="normal"
            class="country__flag"
          />
          {{ data.column.label }}
        </span>
      </template>
      <slot
        slot="table-row"
        slot-scope="data"
      >
        <UserData
          v-if="typeOfTable === 'users'"
          :data="data"
        />
        <PropertiesData
          v-else-if="typeOfTable === 'properties'"
          :data="data"
        />
        <ErrorDashboardData
          v-if="typeOfTable === 'errordashboard'"
          :data="data"
        />
        <AdFormatsData
          v-else-if="typeOfTable === 'adFormats'"
          :data="data"
        />
        <EmailsData
          v-else-if="typeOfTable === 'emails'"
          :data="data"
        />
        <NativeAdsData
          v-else-if="typeOfTable === 'nativeads'"
          :data="data"
        />
        <AdsData
          v-else-if="typeOfTable === 'ads'"
          :data="data"
          :previewAd="previewAd"
        />
        <WithdrawsData
          v-else-if="typeOfTable === 'withdraws'"
          :data="data"
        />
        <AccountsData
          v-else-if="typeOfTable == 'accounts'"
          :data="data"
        />
        <TransactionsData
          v-else-if="typeOfTable == 'transactions'"
          :data="data"
        />
        <TargetingPropertiesData
          v-else-if="typeOfTable == 'targeting'"
          :data="data"
        />
        <DisplayAdsData
          v-else-if="typeOfTable === 'displayads'"
          :data="data"
        />
        <LanguagesData
          v-else-if="typeOfTable == 'languages'"
          :data="data"
        />
        <BidStrategiesSubtypeMappingsData
          v-else-if="typeOfTable == 'bidStrategies'"
          :data="data"
        />
        <ScoringMultipliersData
          v-else-if="typeOfTable == 'multipliers'"
          :data="data"
        />
        <TranslateLanguagesData
          v-else-if="typeOfTable == 'translateLanguages'"
          :data="data"
        />
        <NativeAdPropertiesData
          v-else-if="typeOfTable == 'nativeAdProperties'"
          :data="data"
        />
        <NativeAdMappingsData
          v-else-if="typeOfTable == 'nativeAdMappings'"
          :data="data"
        />
        <DashboardData
          v-else-if="typeOfTable === 'dashboard'"
          :data="data"
        />
        <PrebidData
          v-else-if="typeOfTable === 'prebid'"
          :data="data"
        />
        <InfoData
          v-else-if="typeOfTable === 'info'"
          :data="data"
        />
        <SectionGroupData
          v-else-if="typeOfTable === 'SectionGroup'"
          :data="data"
        />
        <ConfigurationData
          v-else-if="typeOfTable === 'configurationItems'"
          :data="data"
        />
        <IndustriesData
          v-else-if="typeOfTable === 'industryItems'"
          :data="data"
        />
        <ApiKeysData
          v-else-if="typeOfTable === 'apiKeys'"
          :data="data"
        />
        <DailyCostsData
          v-else-if="typeOfTable === 'dailyCosts'"
          :data="data"
        />
        <PerformanceReportData
          v-else-if="typeOfTable === 'performanceReport'"
          :data="data"
          :download-file="downloadFile"
        />
        <accountPropertyTargetingsData
          v-else-if="typeOfTable === 'accountPropertyTargetingsData'"
          :data="data"
        />
      </slot>

      <!-- Empty state slot -->
      <div
        slot="emptystate"
        class="table-empty-content d-flex justify-content-center"
        :class="(typeOfTable == 'targeting' || typeOfTable === 'nativead' || typeOfTable == 'bidStrategies') ? (typeOfTable == 'targeting' ? 'empty-state--targeting' : 'empty-state--full-height') : 'empty-state'"
      >
        <div class="m-auto">
          <GjIcon
            name="Target"
            class="mr-50"
            size="18"
          />
          <span>{{ $tk('ClientPortal.Table.NoDataHaveBeenAddedYet') }}</span>
        </div>
      </div>

      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div
          class="d-flex justify-content-between flex-wrap pagination-wrapper"
        >
          <div class="d-flex border-top admin-table__footer t-pagination">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="pageLength"
              size="sm"
              class="my-0 mr-1"
              hide-goto-end-buttons
              @input="value => { changePage(value); props.pageChanged({currentPage: value})}"
            >
              <template #prev-text>
                <GjIcon
                  class="mr-25"
                  name="ArrowLeft"
                />
              </template>
              <template #next-text>
                <GjIcon
                  class="ml-25"
                  name="ArrowRight"
                />
              </template>
            </b-pagination>
            <span>
              {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }}
              {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }}
              {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }}
              {{ $tk('ClientPortal.Pagination.Items') }}
            </span>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import { computed, ref } from '@vue/composition-api'
import LanguagesData from '@/views/admin/configuration/LanguagesData/LanguagesData.vue'
import NativeAdsData from '@/views/admin/ad-types/NativeAdsData/NativeAdsData.vue'
import AdFormatsData from '@/views/admin/ad-types/AdFormatsData/AdFormatsData.vue'
import UserData from '@/views/admin/management/UsersData/UserData.vue'
import PropertiesData from '@/views/admin/management/PropertiesData/PropertiesData.vue'
import EmailsData from '@/views/admin/configuration/EmailsData/EmailsData.vue'
import AdsData from '@/views/admin/management/AdsData/AdsData.vue'
import WithdrawsData from '@/views/admin/management/WithdrawsData/WithdrawsData.vue'
import AccountsData from '@/views/admin/management/AccountsData/AccountsData.vue'
import TransactionsData from '@/views/admin/management/TransactionsData/TransactionsData.vue'
import BidStrategiesSubtypeMappingsData from '@/views/admin/configuration/BidStrategiesSubtypeMappingsData/BidStrategiesSubtypeMappingsData.vue'
import DisplayAdsData from '@/views/admin/ad-types/DisplayAdsData/DisplayAdsData.vue'
import ScoringMultipliersData from '@/views/admin/configuration/ScoringMultipliersData/ScoringMultipliersData.vue'
import ErrorDashboardData from '@/views/admin/management/ErrorDashboardData/ErrorDashboardData.vue'
import TargetingPropertiesData from '@/views/admin/configuration/TargetingPropertiesData/TargetingPropertiesData.vue'
import TranslateLanguagesData from '@/views/admin/configuration/LanguagesData/TranslateLanguagesData.vue'
import NativeAdMappingsData from '@/views/admin/ad-types/NativeAdsData/NativeAdMappingsData.vue'
import NativeAdPropertiesData from '@/views/admin/ad-types/NativeAdsData/NativeAdPropertiesData.vue'
import DashboardData from '@/views/admin/management/DashboardData/DashboardData.vue'
import PrebidData from '@/views/admin/management/PrebidData/PrebidData.vue'
import InfoData from '@/views/admin/configuration/InfoData/InfoData.vue'
import SectionGroupData from '@/views/admin/configuration/InfoData/SectionGroupData.vue'
import ConfigurationData from '@/views/admin/configuration/ConfigurationData/ConfigurationData.vue'
import ApiKeysData from '@/views/client/account/ApiKeysData.vue'
import CountryFlag from 'vue-country-flag'
import DailyCostsData from '@/views/admin/management/DailyCostsData/DailyCostsData.vue'
import PerformanceReportData from '@/views/admin/management/PerformanceReportData/PerformanceReportData.vue'
import IndustriesData from '@/views/admin/configuration/IndustriesData/IndustriesData.vue'
import accountPropertyTargetingsData from '@/views/admin/management/AccountPropertyTargetingsData/AccountPropertyTargetingsData.vue'

export default {
  components: {
    DailyCostsData,
    PerformanceReportData,
    VueGoodTable,
    GjIcon,
    UserData,
    PropertiesData,
    ErrorDashboardData,
    EmailsData,
    NativeAdsData,
    AdFormatsData,
    AdsData,
    WithdrawsData,
    AccountsData,
    TargetingPropertiesData,
    TransactionsData,
    BidStrategiesSubtypeMappingsData,
    DisplayAdsData,
    LanguagesData,
    ScoringMultipliersData,
    TranslateLanguagesData,
    NativeAdPropertiesData,
    NativeAdMappingsData,
    DashboardData,
    PrebidData,
    CountryFlag,
    InfoData,
    SectionGroupData,
    ConfigurationData,
    ApiKeysData,
    IndustriesData,
    accountPropertyTargetingsData,
  },
  props: {
    // eslint-disable-next-line vue/require-default-prop
    data: {
      type: [Array, Object],
    },
    isLoading: {
      type: [Boolean],
    },
    downloadFile: {
      type: Function,
      required: false,
    },
    // eslint-disable-next-line vue/require-default-prop
    typeOfTable: {
      type: [String],
    },
    enablePagination: {
      type: [Boolean],
      default: true,
    },
    previewAd: {
      type: Function,
      required: false,
    },
    enableSort: {
      type: [Boolean],
      default: true,
    },
    // eslint-disable-next-line vue/require-default-prop
    columns: {
      type: [Array, Object],
    },
    remoteMode: {
      type: [Boolean],
      default: false,
    },
  },
  setup(props, context) {
    const totalRows = computed(() => props.data.totalRecords)
    const currentPage = ref(1)
    const { pageLength } = props.data
    const fromCount = computed(() => (currentPage.value - 1) * pageLength + 1)
    const toCount = computed(() => Math.min(fromCount.value + pageLength - 1, totalRows.value))

    const changePage = value => context.emit('changePage', value)
    const onSortChange = params => context.emit('onSortChange', params)

    return {
      pageLength,
      currentPage,
      fromCount,
      toCount,
      totalRows,
      changePage,
      onSortChange,
    }
  },
}
</script>
<style lang="scss" scoped>
.empty-state {
  height: calc(100vh - 286.5px);
  &--full-height{
    height: calc(100vh - 240.5px)
  }
  &--targeting{
    height: calc(100vh - 193.5px)
  }
}
</style>
